import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, To, useLocation } from "react-router-dom";
import { useWindowSize } from "@app/hooks/useWindowSize";
import type { NavigationItem } from "@app/types/Navigation";
import Icon from "@components/Icon/Icon";
import { isElementInViewport } from "@util/helpers";
import cx from "classnames";

import { getActiveSectionPath } from "./helpers";
import Button from "../AccessibleComponents/Button";

import styles from "./Breadcrumb.module.scss";

export type BreadcrumbProps = {
  parentCategory: string;
  childCategory?: string;
  categoryDataMapping: Record<string, NavigationItem>;
  rootClassName?: string;
};

const excludeCategories = [
  "events-awards",
  "story-threads",
  "keywords",
  "tags",
];

export default function Breadcrumb({
  parentCategory,
  childCategory,
  rootClassName,
  categoryDataMapping,
}: BreadcrumbProps): React.ReactElement {
  const { isScreenLG } = useWindowSize();
  const { pathname } = useLocation();
  const active_section_path = getActiveSectionPath(pathname, childCategory);

  const [scrollIndex, setScrollIndex] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollElementsRef: React.MutableRefObject<Array<HTMLLIElement>> =
    useRef([]);
  const parentCategoryData = categoryDataMapping?.[parentCategory];
  const endIndex = (parentCategoryData?.children ?? []).length - 1;
  const shouldRenderCarousel = contentWidth > containerWidth;
  const activeNavigationItemIndex = childCategory
    ? parentCategoryData?.children?.findIndex(
        (fullChildCategoryPath: string) => {
          return `${parentCategory}/${childCategory}` === fullChildCategoryPath;
        }
      ) ?? 0
    : 0;

  const handleNav = (newIndex: number, direction: "prev" | "next") => {
    /**
     * When new scroll index hits the boundary, stop checking
     */
    if (newIndex === -1) return;
    if (newIndex === endIndex + 1) return;

    if (newIndex === 0 || newIndex === endIndex) {
      handleScroll(newIndex);
      return;
    }

    const element = scrollElementsRef.current[newIndex];

    if (element === undefined) return;

    if (scrollElementsRef.current && isElementInViewport(element)) {
      handleNav(newIndex + (direction === "next" ? 1 : -1), direction);
      return;
    }

    handleScroll(newIndex);
  };

  const handleScroll = (indexToScroll: number) => {
    if (scrollElementsRef && scrollElementsRef.current) {
      setTimeout(() => {
        scrollElementsRef.current[indexToScroll]?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }, 0);
      setScrollIndex(indexToScroll);
    }
  };

  const withCarousel = (carouselContent: React.ReactNode) => {
    return (
      <>
        <div>
          <Button>
            <Icon
              onClick={() => {
                handleNav(scrollIndex - 1, "prev");
              }}
              type="arrowLeftCircle"
              rootClassName="absolute left-0 top-[9px] z-10 block opacity-70 fill-gray-650 cursor-pointer hover:opacity-30"
              size={27}
              aria-label="left arrow"
            />
          </Button>
          <Button>
            <Icon
              type="arrowRightCircle"
              rootClassName={cx(
                "absolute right-0 top-[9px] z-10 block opacity-70 fill-gray-650 cursor-pointer hover:opacity-30"
              )}
              onClick={() => {
                handleNav(scrollIndex + 1, "next");
              }}
              size={27}
              aria-label="right arrow"
            />
          </Button>
        </div>
        <div
          className={cx(
            styles.carouselWrapper,
            "relative flex overflow-hidden"
          )}
        >
          <ul
            className={cx(
              "breadcrumb-scroll-container",
              styles.scrollWrapper,
              "relative flex flex-row items-center gap-4 overflow-y-hidden overflow-x-scroll px-[52px]"
            )}
          >
            {carouselContent}
          </ul>
        </div>
      </>
    );
  };

  const withoutCarousel = (content: React.ReactNode) => {
    return (
      <ul
        ref={(element) => {
          if (element && element.clientWidth) {
            setContentWidth(element.clientWidth);
          }
        }}
        className={cx(
          styles.scrollWrapper,
          "flex shrink-0 flex-row items-center gap-4"
        )}
      >
        {content}
      </ul>
    );
  };

  const isLinkActive = (link: To) => {
    return active_section_path === link;
  };

  const renderChildren = () => {
    return parentCategoryData?.children?.map(
      (fullChildCategoryPath: string, index: number) => {
        let isCategoryExcluded = false;

        excludeCategories.forEach((category) => {
          if (fullChildCategoryPath.startsWith(category)) {
            isCategoryExcluded = true;
          }
        });

        const childCategoryData = categoryDataMapping?.[fullChildCategoryPath];

        return !isCategoryExcluded && childCategoryData ? (
          <li
            className={cx(
              fullChildCategoryPath.replace("/", "-"),
              isLinkActive(childCategoryData.link) && styles.active,
              "border-box h-full shrink-0 !leading-[45px]"
            )}
            key={fullChildCategoryPath}
            ref={(element) => {
              scrollElementsRef.current[index] = element as HTMLLIElement;
            }}
          >
            <Link to={childCategoryData.link} reloadDocument>
              {childCategoryData.label}
            </Link>
          </li>
        ) : null;
      }
    );
  };

  useEffect(() => {
    handleScroll(activeNavigationItemIndex);
  }, [activeNavigationItemIndex, isScreenLG]);

  useEffect(() => {
    setContentWidth(0);
  }, [parentCategory]);

  return (
    <div
      className={cx(
        styles.componentBreadcrumb,
        "flex h-[45px] flex-row max-lg:overflow-x-scroll",
        rootClassName
      )}
    >
      {parentCategoryData ? (
        <h3
          className={cx(
            styles.parent,
            "relative mr-[32px] box-border shrink-0 pr-[16px] !leading-[45px]",
            isLinkActive(parentCategoryData.link) && styles.active
          )}
        >
          <Link to={parentCategoryData.link} reloadDocument>
            {parentCategoryData.label}
          </Link>
        </h3>
      ) : null}

      {parentCategoryData?.children &&
      parentCategoryData?.children.length > 0 ? (
        <div
          ref={(element) => {
            if (element && element.clientWidth) {
              setContainerWidth(element.clientWidth);
            }
          }}
          className={cx(
            styles.children,
            "flex flex-col overflow-hidden max-lg:shrink-0"
          )}
        >
          <div className="relative flex h-full" aria-label="breadcrumbs">
            {!isScreenLG || !shouldRenderCarousel
              ? withoutCarousel(renderChildren())
              : withCarousel(renderChildren())}
          </div>
        </div>
      ) : null}
    </div>
  );
}
